import React from "react";
import PropTypes from "prop-types";
import { Typography, Box, TextField } from "@material-ui/core";
import { COLORS, WIZARD_STEPS, APPLY_MODES, SPACING } from "../constants";
import { findNode } from "../utils";
import { Btn, SectionHeading, Badge } from "../components/UI";
import { LocationTree } from "../components/LocationTree";
import { useStyles } from "./wizard-view.style";

const LOCATION_ICONS = ["🏛️", "🏢", "📍"];

function serviceDisplayName(templateService, servicesRef) {
  if (templateService.serviceId == null) return null;
  const ref = servicesRef.find((service) => service.id === templateService.serviceId);
  return ref?.displayName ?? ref?.name ?? String(templateService.serviceId);
}

function resourceDisplayName(templateResource, resourcesRef) {
  if (templateResource.resourceId == null) return null;
  const ref = resourcesRef.find((resource) => resource.id === templateResource.resourceId);
  return ref?.name ?? String(templateResource.resourceId);
}

function WizardDoneStep({ wizardSelectedTemplate, wizardLeafs, wizardMode, wizardApplyErrors, wizardUnresolvedServiceIds, wizardUnresolvedResourceIds, wizardReset, setTab }) {
  const classes = useStyles();
  return (
    <Box className={classes.doneContainer}>
      <Box className={classes.doneIcon}>✓</Box>
      <Typography variant="h6" sx={{ fontWeight: 700, color: "#2e7d32", marginBottom: `${SPACING.formGroupGap}px` }}>
        Applied Successfully
      </Typography>
      <Typography variant="body2" sx={{ color: COLORS.muted, marginBottom: `${SPACING.formGroupGap}px` }}>
        &ldquo;{wizardSelectedTemplate?.name}&rdquo; applied to {wizardLeafs.length} location(s) via {wizardMode}.
      </Typography>
      {wizardApplyErrors.length > 0 && (
        <Box className={classes.errorBox}>
          <Typography variant="caption" sx={{ fontWeight: 600, color: COLORS.warn, marginBottom: '8px', display: 'block' }}>
            {wizardApplyErrors.length} location(s) failed
          </Typography>
          <Box component="ul" sx={{ margin: 0, paddingLeft: '20px', fontSize: 12, color: COLORS.body }}>
            {wizardApplyErrors.map((err) => (
              <li key={`${err.locationId}-${err.message}`}>
                <strong>{err.locationId}</strong>: {err.message}
              </li>
            ))}
          </Box>
        </Box>
      )}
      {(wizardUnresolvedServiceIds?.length > 0 || wizardUnresolvedResourceIds?.length > 0) && (
        <Box className={classes.infoBox}>
          <Typography variant="caption" sx={{ fontWeight: 600, color: COLORS.info, marginBottom: '6px', display: 'block' }}>
            Skipped (not in catalog)
          </Typography>
          {wizardUnresolvedServiceIds?.length > 0 && (
            <Typography variant="caption">Services (IDs): {wizardUnresolvedServiceIds.join(", ")}</Typography>
          )}
          {wizardUnresolvedResourceIds?.length > 0 && (
            <Typography variant="caption">Resources (IDs): {wizardUnresolvedResourceIds.join(", ")}</Typography>
          )}
        </Box>
      )}
      <Box className={classes.doneActions}>
        <Btn variant="outlined" onClick={wizardReset}>Apply Another</Btn>
        <Btn onClick={() => { setTab("browse"); wizardReset(); }}>Browse Locations</Btn>
      </Box>
    </Box>
  );
}

function WizardTemplateStep({ templates, wizardTemplateId, setWizardTemplateId, openNewTemplate }) {
  const classes = useStyles();
  return (
    <Box>
      <SectionHeading>Choose a Template</SectionHeading>
      {templates.map((template) => {
        const isSelected = wizardTemplateId === template.id;
        return (
          <button
            type="button"
            key={template.id}
            onClick={() => setWizardTemplateId(template.id)}
            className={classes.templateOption}
            style={{
              background: isSelected ? COLORS.primaryLight : COLORS.white,
              border: `2px solid ${isSelected ? COLORS.primary : COLORS.border}`,
            }}
          >
            <Box
              className={classes.radioCircle}
              sx={{ border: `2px solid ${isSelected ? COLORS.primary : COLORS.border}` }}
            >
              {isSelected && <Box className={classes.radioFill} />}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.body, marginBottom: '4px' }}>
                {template.name}
              </Typography>
              <Typography variant="caption" sx={{ color: COLORS.muted, marginBottom: `${SPACING.detailGap}px`, display: 'block' }}>
                {template.desc}
              </Typography>
              <Box sx={{ display: "flex", gap: `${SPACING.detailGap}px` }}>
                <Typography variant="caption" sx={{ color: COLORS.primary }}>{template.services.length} service{template.services.length === 1 ? "" : "s"}</Typography>
                <Typography variant="caption" sx={{ color: COLORS.primary }}>{template.resources.length} resource{template.resources.length === 1 ? "" : "s"}</Typography>
              </Box>
            </Box>
          </button>
        );
      })}
      <button type="button" onClick={openNewTemplate} className={classes.createTemplateButton}>
        <Typography variant="body2" sx={{ color: COLORS.primary, fontWeight: 600 }}>
          + Create New Template
        </Typography>
      </button>
    </Box>
  );
}

function WizardLocationStep({ wizardSearch, setWizardSearch, wizardChecked, wizardCheck, wizardExpanded, toggleWizardExpanded, wizardLeafs, locations }) {
  const classes = useStyles();
  return (
    <Box className={classes.locationStepRoot}>
      <Box className={classes.locationSidebar}>
        <Box className={classes.locationSidebarSearch}>
          <TextField
            placeholder="Search..."
            value={wizardSearch}
            onChange={(event) => setWizardSearch(event.target.value)}
            fullWidth
            variant="outlined"
            size="small"
          />
        </Box>
        <Box className={classes.locationTreeScroll}>
          <LocationTree
            locations={locations}
            search={wizardSearch}
            mode="select"
            checked={wizardChecked}
            onCheck={wizardCheck}
            expanded={wizardExpanded}
            onToggle={toggleWizardExpanded}
          />
        </Box>
      </Box>
      <Box sx={{ flex: 1 }}>
        <SectionHeading>
          Selected ({wizardLeafs.length} target{wizardLeafs.length === 1 ? "" : "s"})
        </SectionHeading>
        {wizardChecked.size === 0 && (
          <Typography
            sx={{
              padding: '20px',
              textAlign: "center",
              color: COLORS.muted,
              fontSize: 13,
              background: COLORS.white,
              borderRadius: '6px',
              border: `1px dashed ${COLORS.border}`,
            }}
          >
            Select locations from the tree.
          </Typography>
        )}
        {wizardChecked.size > 0 && (
          <Box className={classes.selectedChips}>
            {[...wizardChecked].map((id) => {
              const node = findNode(locations, id);
              if (!node) return null;
              return (
                <Box key={id} component="span" className={classes.selectedChip}>
                  {LOCATION_ICONS[node.level]} {node.name}{" "}
                  <button
                    type="button"
                    onClick={() => wizardCheck(id, [id])}
                    className={classes.chipRemoveButton}
                  >
                    ×
                  </button>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
}

function WizardModeStep({ wizardMode, setWizardMode }) {
  const classes = useStyles();
  return (
    <Box>
      <SectionHeading>Apply Mode</SectionHeading>
      <Typography variant="body2" sx={{ color: COLORS.muted, marginBottom: `${SPACING.detailGap}px` }}>
        Overlapping configurations detected. Choose how to handle them.
      </Typography>
      {APPLY_MODES.map((mode) => {
        const isSelected = wizardMode === mode.id;
        return (
          <button
            type="button"
            key={mode.id}
            onClick={() => setWizardMode(mode.id)}
            className={classes.modeOption}
            style={{
              background: isSelected ? COLORS.primaryLight : COLORS.white,
              border: `2px solid ${isSelected ? COLORS.primary : COLORS.border}`,
            }}
          >
            <Box
              className={classes.radioCircle}
              sx={{ border: `2px solid ${isSelected ? COLORS.primary : COLORS.border}`, marginTop: '2px' }}
            >
              {isSelected && <Box className={classes.radioFill} />}
            </Box>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.body }}>
                {mode.title}
              </Typography>
              <Typography variant="caption" sx={{ color: COLORS.muted, marginTop: '2px', lineHeight: 1.4, display: 'block' }}>
                {mode.description}
              </Typography>
            </Box>
          </button>
        );
      })}
    </Box>
  );
}

function WizardReviewStep({ wizardSelectedTemplate, wizardLeafs, wizardMode, servicesRef, resourcesRef }) {
  const classes = useStyles();
  return (
    <Box>
      <SectionHeading>Review &amp; Confirm</SectionHeading>
      <Box className={classes.reviewCard}>
        <Box className={classes.reviewGrid}>
          <Box>
            <Typography className={classes.reviewLabel}>Template</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{wizardSelectedTemplate?.name}</Typography>
            <Typography variant="caption" sx={{ color: COLORS.muted }}>
              {wizardSelectedTemplate?.services.length} service{wizardSelectedTemplate?.services.length === 1 ? "" : "s"}, {wizardSelectedTemplate?.resources.length} resource{wizardSelectedTemplate?.resources.length === 1 ? "" : "s"}
            </Typography>
          </Box>
          <Box>
            <Typography className={classes.reviewLabel}>Locations</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {wizardLeafs.length} target{wizardLeafs.length === 1 ? "" : "s"}
            </Typography>
          </Box>
          <Box>
            <Typography className={classes.reviewLabel}>Mode</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {wizardMode === "merge" ? "Merge" : "Overwrite"}
            </Typography>
          </Box>
        </Box>
        <Typography className={classes.reviewSectionLabel}>Services:</Typography>
        <Box className={classes.reviewBadges}>
          {wizardSelectedTemplate?.services.map((service) => (
            <Badge key={service.serviceId ?? service._key} type="tag">
              {serviceDisplayName(service, servicesRef) ?? `Service ${service.serviceId}`}
            </Badge>
          ))}
          {!wizardSelectedTemplate?.services.length && (
            <Typography variant="caption" sx={{ color: COLORS.muted }}>None</Typography>
          )}
        </Box>
        <Typography className={classes.reviewSectionLabel}>Resources:</Typography>
        <Box className={classes.reviewBadges}>
          {wizardSelectedTemplate?.resources.map((resource) => (
            <Badge key={resource.resourceId ?? resource._key} type="tag">
              {resourceDisplayName(resource, resourcesRef) ?? `Resource ${resource.resourceId}`}
            </Badge>
          ))}
          {!wizardSelectedTemplate?.resources.length && (
            <Typography variant="caption" sx={{ color: COLORS.muted }}>None</Typography>
          )}
        </Box>
        <Box
          className={classes.reviewWarning}
          sx={{
            background: wizardMode === "overwrite" ? COLORS.warnLt : COLORS.infoLt,
            color: wizardMode === "overwrite" ? "#e65100" : COLORS.info,
          }}
        >
          {wizardMode === "overwrite" && (
            <Typography variant="caption">
              Overwrite will remove all direct configs at target locations before applying the template.
            </Typography>
          )}
          {wizardMode !== "overwrite" && (
            <Typography variant="caption">
              Merge will add new entries and update overlapping ones. Non-conflicting entries are preserved.
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}

function getStepBackground(wizardDone, stepIndex, currentStep) {
  if (wizardDone) return COLORS.success;
  return stepIndex <= currentStep ? COLORS.primary : COLORS.border;
}

function WizardStepContent({ wizardDone, wizardStep, ...props }) {
  if (wizardDone) return <WizardDoneStep {...props} />;
  if (wizardStep === 0) return <WizardTemplateStep {...props} />;
  if (wizardStep === 1) return <WizardLocationStep {...props} />;
  if (wizardStep === 2) return <WizardModeStep {...props} />;
  return <WizardReviewStep {...props} />;
}

const wizardSelectedTemplateShape = PropTypes.shape({
  name: PropTypes.string,
  services: PropTypes.array,
  resources: PropTypes.array,
});

WizardDoneStep.propTypes = {
  wizardSelectedTemplate: wizardSelectedTemplateShape,
  wizardLeafs: PropTypes.array,
  wizardMode: PropTypes.string,
  wizardApplyErrors: PropTypes.array,
  wizardUnresolvedServiceIds: PropTypes.array,
  wizardUnresolvedResourceIds: PropTypes.array,
  wizardReset: PropTypes.func.isRequired,
  setTab: PropTypes.func.isRequired,
};

WizardTemplateStep.propTypes = {
  templates: PropTypes.array,
  wizardTemplateId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setWizardTemplateId: PropTypes.func.isRequired,
  openNewTemplate: PropTypes.func.isRequired,
};

WizardLocationStep.propTypes = {
  wizardSearch: PropTypes.string,
  setWizardSearch: PropTypes.func.isRequired,
  wizardChecked: PropTypes.instanceOf(Set),
  wizardCheck: PropTypes.func.isRequired,
  wizardExpanded: PropTypes.instanceOf(Set),
  toggleWizardExpanded: PropTypes.func.isRequired,
  wizardLeafs: PropTypes.array,
  locations: PropTypes.array,
};

WizardModeStep.propTypes = {
  wizardMode: PropTypes.string,
  setWizardMode: PropTypes.func.isRequired,
};

WizardReviewStep.propTypes = {
  wizardSelectedTemplate: wizardSelectedTemplateShape,
  wizardLeafs: PropTypes.array,
  wizardMode: PropTypes.string,
  servicesRef: PropTypes.array,
  resourcesRef: PropTypes.array,
};

WizardStepContent.propTypes = {
  wizardDone: PropTypes.bool,
  wizardStep: PropTypes.number,
};

export function WizardView({
  wizardStep,
  wizardDone,
  wizardApplyErrors,
  wizardUnresolvedServiceIds,
  wizardUnresolvedResourceIds,
  wizardTemplateId,
  setWizardTemplateId,
  wizardChecked,
  wizardExpanded,
  wizardSearch,
  setWizardSearch,
  wizardMode,
  setWizardMode,
  wizardSelectedTemplate,
  wizardLeafs,
  wizardOverlap,
  wizardNext,
  wizardPrev,
  wizardCanGo,
  wizardReset,
  wizardCheck,
  applyWizard,
  wizardStartApply,
  setTab,
  templates,
  openNewTemplate,
  toggleWizardExpanded,
  locations,
  servicesRef,
  resourcesRef,
}) {
  const classes = useStyles();

  return (
    <Box className={classes.root}>
      <Box className={classes.stepperBar}>
        <Box className={classes.stepperRow}>
          {WIZARD_STEPS.map((stepLabel, stepIndex) => {
            const skipMode = stepIndex === 2 && !wizardOverlap;
            const isActiveOrPast = stepIndex <= wizardStep || wizardDone;
            return (
              <Box
                key={stepLabel}
                className={classes.stepItem}
                sx={{ opacity: skipMode ? 0.35 : 1 }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: '5px' }}>
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '12px',
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 11,
                      fontWeight: 700,
                      background: getStepBackground(wizardDone, stepIndex, wizardStep),
                      color: isActiveOrPast ? "#fff" : COLORS.muted,
                    }}
                  >
                    {wizardDone ? "✓" : stepIndex + 1}
                  </Box>
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{
                      fontWeight: stepIndex === wizardStep ? 700 : 400,
                      color: stepIndex <= wizardStep ? COLORS.primary : COLORS.muted,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {stepLabel}
                  </Typography>
                </Box>
                {stepIndex < 3 && (
                  <Box
                    className={classes.stepConnector}
                    sx={{ background: stepIndex < wizardStep ? COLORS.primary : COLORS.border }}
                  />
                )}
              </Box>
            );
          })}
        </Box>
      </Box>
      <Box className={classes.scrollContent}>
        <WizardStepContent
          wizardDone={wizardDone}
          wizardStep={wizardStep}
          wizardSelectedTemplate={wizardSelectedTemplate}
          wizardLeafs={wizardLeafs}
          wizardMode={wizardMode}
          wizardApplyErrors={wizardApplyErrors}
          wizardUnresolvedServiceIds={wizardUnresolvedServiceIds}
          wizardUnresolvedResourceIds={wizardUnresolvedResourceIds}
          wizardReset={wizardReset}
          setTab={setTab}
          templates={templates}
          wizardTemplateId={wizardTemplateId}
          setWizardTemplateId={setWizardTemplateId}
          openNewTemplate={openNewTemplate}
          wizardSearch={wizardSearch}
          setWizardSearch={setWizardSearch}
          wizardChecked={wizardChecked}
          wizardCheck={wizardCheck}
          wizardExpanded={wizardExpanded}
          toggleWizardExpanded={toggleWizardExpanded}
          locations={locations}
          setWizardMode={setWizardMode}
          servicesRef={servicesRef}
          resourcesRef={resourcesRef}
        />
      </Box>
      {!wizardDone && (
        <Box className={classes.footer}>
          <Btn variant="outlined" onClick={wizardPrev} disabled={wizardStep === 0}>
            ← Back
          </Btn>
          <Box className={classes.footerRight}>
            <Btn variant="outlined" onClick={() => { setTab("browse"); wizardReset(); }}>
              Cancel
            </Btn>
            {wizardStep < 3 && (
              <Btn onClick={wizardNext} disabled={!wizardCanGo}>Next →</Btn>
            )}
            {wizardStep >= 3 && (
              <Btn onClick={applyWizard} sx={{ background: "#2e7d32" }}>✓ Apply</Btn>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
}

WizardView.propTypes = {
  wizardStep: PropTypes.number.isRequired,
  wizardDone: PropTypes.bool,
  wizardApplyErrors: PropTypes.array,
  wizardUnresolvedServiceIds: PropTypes.array,
  wizardUnresolvedResourceIds: PropTypes.array,
  wizardTemplateId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setWizardTemplateId: PropTypes.func.isRequired,
  wizardChecked: PropTypes.instanceOf(Set),
  wizardExpanded: PropTypes.instanceOf(Set),
  wizardSearch: PropTypes.string,
  setWizardSearch: PropTypes.func.isRequired,
  wizardMode: PropTypes.string,
  setWizardMode: PropTypes.func.isRequired,
  wizardSelectedTemplate: wizardSelectedTemplateShape,
  wizardLeafs: PropTypes.array,
  wizardOverlap: PropTypes.bool,
  wizardNext: PropTypes.func.isRequired,
  wizardPrev: PropTypes.func.isRequired,
  wizardCanGo: PropTypes.bool,
  wizardReset: PropTypes.func.isRequired,
  wizardCheck: PropTypes.func.isRequired,
  applyWizard: PropTypes.func.isRequired,
  wizardStartApply: PropTypes.func.isRequired,
  setTab: PropTypes.func.isRequired,
  templates: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    desc: PropTypes.string,
    services: PropTypes.array,
    resources: PropTypes.array,
  })),
  openNewTemplate: PropTypes.func.isRequired,
  toggleWizardExpanded: PropTypes.func.isRequired,
  locations: PropTypes.array,
  servicesRef: PropTypes.array,
  resourcesRef: PropTypes.array,
};

WizardView.defaultProps = {
  wizardDone: false,
  wizardApplyErrors: [],
  wizardUnresolvedServiceIds: [],
  wizardUnresolvedResourceIds: [],
  wizardTemplateId: null,
  wizardChecked: new Set(),
  wizardExpanded: new Set(),
  wizardSearch: "",
  wizardMode: "merge",
  wizardSelectedTemplate: null,
  wizardLeafs: [],
  wizardOverlap: false,
  wizardCanGo: false,
  templates: [],
  locations: [],
  servicesRef: [],
  resourcesRef: [],
};
